import React from 'react';
import { motion } from 'framer-motion';
import ReviewWorkflowActions from './ReviewWorkflowActions';
import {
  Calendar,
  CheckCircle,
  Clock,
  Archive,
  Eye,
  Lock,
  AlertCircle,
  Globe,
  Smartphone,
  Monitor,
  Tablet,
  BookOpen,
  Building,
  Download,
  Settings,
  Trash2
} from 'lucide-react';

/**
 * Clean Assessment Card with Dropdown Variant Selector
 * Shows only primary assessments in grid, variants accessible via dropdown
 */
const CleanAssessmentCard = ({
  assessment,
  onEdit,
  onImport,
  onManage,
  onManageVPG,
  onPreview,
  onDelete,
  formatDate,
  viewMode = 'grid',
  isVPGLibrary = false,
  isSuperuser = false,
  onReviewChanged,
  isApprover = false,
  onReview
}) => {
  // Logged-in user id, used to scope review-workflow actions (submitter-only).
  let currentUserId = null;
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    currentUserId = u?.id ?? u?.user_id ?? u?.pk ?? null;
  } catch {
    currentUserId = null;
  }

  // Debug logging for VPG cards
  if (isVPGLibrary) {
    console.log('VPG Card Props:', {
      assessmentName: assessment.name,
      isVPGLibrary,
      isSuperuser,
      hasOnPreview: !!onPreview,
      hasOnEdit: !!onEdit,
      hasOnDelete: !!onDelete
    });
  }

  // Check if this is a primary assessment with variants
  const isPrimary = assessment.variants && assessment.variants.length > 0;
  const variantCount = assessment.variants?.length || 0;

  // Status configuration: dot chip tone per status (same mapping across the builder)
  const getStatusConfig = (status) => {
    const configs = {
      draft: { icon: Clock, badge: 'badge-warning', label: 'Draft' },
      review: { icon: Eye, badge: 'badge-info', label: 'Pending Review' },
      in_review: { icon: Eye, badge: 'badge-info', label: 'Pending Review' },
      approved: {
        icon: CheckCircle,
        badge: 'badge-teal',
        label: 'Approved'
      },
      denied: { icon: AlertCircle, badge: 'badge-danger', label: 'Changes Requested' },
      published: { icon: CheckCircle, badge: 'badge-success', label: 'Published' },
      archived: { icon: Archive, badge: 'badge-gray', label: 'Archived' }
    };
    return configs[status] || configs.draft;
  };

  const statusConfig = getStatusConfig(assessment.publication_status || assessment.survey?.status || assessment.status);

  // Locked label = in review, awaiting a decision (editing is read-only, FECOA-1614).
  // Approved is intentionally excluded: it's not a dead-end, it's ready to publish,
  // so it falls through to the Publish action in ReviewWorkflowActions instead.
  const isLocked = assessment.publication_status === 'in_review';

  // FECOA-1302/1590: another user holds the exclusive edit lock. Show the
  // "currently being edited by X" banner and drop this viewer into View Only.
  const editLock =
    assessment.edit_lock && !assessment.edit_lock.is_self ? assessment.edit_lock : null;
  const formatLockTime = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString([], {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      });
    } catch {
      return '';
    }
  };
  const lockBanner = editLock ? (
    <div className="flex items-center gap-1.5 border-b border-warning-500/30 bg-warning-50 px-4 py-2 text-xs font-medium text-warning-800 dark:bg-warning-500/10 dark:text-warning-400">
      <Lock className="h-3.5 w-3.5 flex-shrink-0" />
      <span className="truncate">
        Currently being edited by <strong className="font-semibold">{editLock.locked_by_name}</strong>
        {editLock.acquired_at ? ` (since ${formatLockTime(editLock.acquired_at)})` : ''}
      </span>
    </div>
  ) : null;

  // Library type chip configuration (gray chips with an icon)
  const getLibraryConfig = (libraryType) => {
    const configs = {
      vpg: { icon: Globe, label: 'VPG' },
      sponsor: { icon: Building, label: 'Sponsor' },
      study: { icon: BookOpen, label: 'Study' }
    };
    return configs[libraryType];
  };

  const libraryConfig = getLibraryConfig(assessment.library_type);
  const LibraryIcon = libraryConfig?.icon;

  // FECOA-1526: respondent type (COA type) badge — PRO / ObsRO / ClinRO
  const respondentType =
    assessment.respondent_type ||
    assessment.survey?.respondent_type ||
    assessment.survey_detail?.respondent_type;
  const respondentBadgeLabel = {
    participant: 'PRO',
    caregiver: 'ObsRO',
    clinician: 'ClinRO',
  }[respondentType];
  const respondentBadge = respondentBadgeLabel ? (
    <span className="badge badge-gray">
      {respondentBadgeLabel}
    </span>
  ) : null;

  const handleCardClick = () => {
    if (isVPGLibrary) {
      // For VPG library: superusers see management modal, others see import modal
      if (isSuperuser && onManageVPG) {
        onManageVPG(assessment);
      } else {
        onImport && onImport(assessment);
      }
    } else if (onManage) {
      // Clicking a card opens its details; editing is an explicit action from
      // there (and review-locked assessments open read-only either way).
      onManage(assessment);
    } else {
      // Fallback for any card rendered without a details handler.
      onEdit && onEdit(assessment);
    }
  };

  // Grid View
  if (viewMode === 'grid') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className={`surface-card-interactive group relative flex cursor-pointer flex-col overflow-hidden ${
          editLock ? 'border-warning-500/40 dark:border-warning-500/40' : ''
        }`}
        onClick={handleCardClick}
      >
        {lockBanner}
        <div className="flex flex-1 flex-col p-5">
          {/* Status, library and respondent type */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={`badge badge-dot ${statusConfig.badge}`}>{statusConfig.label}</span>

            {/* Library badge */}
            {libraryConfig && (
              <span className="badge badge-gray">
                <LibraryIcon className="h-3 w-3" aria-hidden="true" />
                <span>{libraryConfig.label}</span>
              </span>
            )}

            {/* Respondent (COA) type badge */}
            {respondentBadge}
          </div>

          {/* Title */}
          <h3 className="mt-3 line-clamp-2 text-[15px] font-semibold leading-snug text-gray-900 dark:text-gray-50">
            {assessment.name || assessment.survey?.name || 'Untitled Assessment'}
          </h3>

          {/* Metadata row - version, type, variants, and date combined */}
          <div className="mb-4 mt-2.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 text-[12.5px] text-gray-500 dark:text-gray-400">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="code-chip">v{assessment.version || '1.0'}</span>
              <span className="badge badge-gray">{assessment.assessment_type === 'fixed_form' ? 'Fixed Form' : 'Item Library'}</span>
              {isPrimary && variantCount > 0 && (
                <span className="badge badge-brand">{variantCount} variant{variantCount !== 1 ? 's' : ''}</span>
              )}
            </div>
            <div className="flex flex-shrink-0 items-center gap-1.5">
              <Calendar className="h-4 w-4 text-gray-400 dark:text-gray-500" aria-hidden="true" />
              <span className="whitespace-nowrap">{formatDate(assessment.builder_metadata?.last_saved || assessment.modified)}</span>
            </div>
          </div>

          {/* Actions footer, wraps so the action cluster never clips on
              narrow cards (e.g. Locked + Awaiting review + Recall + History + Details) */}
          <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3 dark:border-white/[0.06]">
            {isVPGLibrary ? (
              /* VPG Library: Show preview and copy text, plus superuser controls */
              <React.Fragment>
                <span className="text-[13px] font-medium text-primary-700 dark:text-primary-400">Copy to Library</span>
                <div className="flex items-center gap-1">
                  {isSuperuser && onEdit && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(assessment);
                      }}
                      className="btn btn-ghost btn-sm"
                      title="Edit (Superuser)"
                    >
                      <Settings className="h-3.5 w-3.5" />
                      <span>Edit</span>
                    </button>
                  )}
                  {assessment?.can_delete && onDelete && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(assessment);
                      }}
                      className="btn btn-danger-soft btn-sm"
                      title="Archive"
                    >
                      <Archive className="h-3.5 w-3.5" />
                      <span>Archive</span>
                    </button>
                  )}
                  {onPreview && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onPreview(assessment);
                      }}
                      className="btn btn-ghost btn-sm"
                      title="View assessment details"
                    >
                      <Eye className="h-3.5 w-3.5" />
                      <span>Preview</span>
                    </button>
                  )}
                </div>
              </React.Fragment>
            ) : (
              /* Non-VPG: Show edit/view action */
              <>
                {isLocked ? (
                  <span className="flex items-center gap-1.5 text-[13px] font-medium text-gray-400 dark:text-gray-500" title="Locked while under review">
                    <Lock className="h-3.5 w-3.5" />
                    Locked
                  </span>
                ) : editLock ? (
                  <span className="flex items-center gap-1.5 text-[13px] font-medium text-warning-600 dark:text-warning-400" title={`Being edited by ${editLock.locked_by_name}`}>
                    <Eye className="h-3.5 w-3.5" />
                    View Only
                  </span>
                ) : (
                  <span className="text-[13px] font-medium text-primary-700 dark:text-primary-400">
                    View Details
                  </span>
                )}
                <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-1.5">
                  <ReviewWorkflowActions
                    entity={assessment}
                    currentUserId={currentUserId}
                    isApprover={isApprover}
                    onReview={onReview}
                    onChanged={onReviewChanged}
                    stopPropagation
                  />
                  {onManage && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onManage(assessment);
                      }}
                      className="btn btn-ghost btn-sm"
                      title="View Details"
                    >
                      <Settings className="h-3.5 w-3.5" />
                      <span>Details</span>
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  // List View (a row inside the section's list card)
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className="group cursor-pointer transition-colors hover:bg-gray-50/80 dark:hover:bg-white/[0.03]"
      onClick={handleCardClick}
    >
      {lockBanner}
      <div className="flex flex-col gap-3 px-5 py-3.5 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
        {/* Left section: Name and Metadata */}
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold text-gray-900 dark:text-gray-50">
            {assessment.name || assessment.survey?.name || 'Untitled Assessment'}
          </h3>

          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <span className="code-chip">v{assessment.version || '1.0'}</span>
            <span className="badge badge-gray">{assessment.assessment_type === 'fixed_form' ? 'Fixed Form' : 'Item Library'}</span>
            {isPrimary && variantCount > 0 && (
              <span className="badge badge-brand">{variantCount} variant{variantCount !== 1 ? 's' : ''}</span>
            )}
          </div>
        </div>

        {/* Right section: Library, Status, Date, Manage button */}
        <div className="flex flex-wrap items-center gap-2 lg:flex-shrink-0 lg:justify-end">
          {/* Library badge */}
          {libraryConfig && (
            <span className="badge badge-gray">
              <LibraryIcon className="h-3 w-3" aria-hidden="true" />
              <span>{libraryConfig.label}</span>
            </span>
          )}

          {/* Status badge */}
          <span className={`badge badge-dot ${statusConfig.badge}`}>{statusConfig.label}</span>

          {/* Respondent (COA) type badge */}
          {respondentBadge}

          {/* Last updated */}
          <div className="flex min-w-[110px] items-center gap-1.5 text-[12.5px] text-gray-500 dark:text-gray-400">
            <Calendar className="h-4 w-4 text-gray-400 dark:text-gray-500" aria-hidden="true" />
            <span className="whitespace-nowrap">{formatDate(assessment.builder_metadata?.last_saved || assessment.modified)}</span>
          </div>

          {/* Action buttons */}
          {isVPGLibrary ? (
            /* Preview and superuser buttons for VPG */
            <div className="flex items-center gap-1">
                  {isSuperuser && onEdit && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(assessment);
                      }}
                      className="btn btn-ghost btn-sm"
                      title="Edit (Superuser)"
                    >
                      <Settings className="h-3.5 w-3.5" />
                      <span>Edit</span>
                    </button>
                  )}
                  {assessment?.can_delete && onDelete && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(assessment);
                      }}
                      className="btn btn-danger-soft btn-sm"
                      title="Archive"
                    >
                      <Archive className="h-3.5 w-3.5" />
                      <span>Archive</span>
                    </button>
                  )}
                  {onPreview && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onPreview(assessment);
                      }}
                      className="btn btn-ghost btn-sm"
                      title="View assessment details"
                    >
                      <Eye className="h-3.5 w-3.5" />
                      <span>Preview</span>
                    </button>
                  )}
            </div>
          ) : (
            /* Review actions + Details button for non-VPG */
            <div className="flex flex-wrap items-center gap-1.5">
                  <ReviewWorkflowActions
                    entity={assessment}
                    currentUserId={currentUserId}
                    isApprover={isApprover}
                    onReview={onReview}
                    onChanged={onReviewChanged}
                    stopPropagation
                  />
                  {onManage && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onManage(assessment);
                      }}
                      className="btn btn-ghost btn-sm"
                      title="View Details"
                    >
                      <Settings className="h-3.5 w-3.5" />
                      <span>Details</span>
                    </button>
                  )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default CleanAssessmentCard;
